import { createFileRoute, Link } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { EduShell, EmptyState, TEACHER_NAV } from "@/components/EduShell";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { eduRpc, GRADE_LEVELS, SUBJECTS } from "@/lib/education";

import { T } from "@/components/T";
import i18n from "@/i18n";
export const Route = createFileRoute("/teacher/classes/")({
  component: TeacherClasses,
  head: () => ({
    meta: [
      { title: "Classes - Inquira teacher" },
      { name: "description", content: "Create and manage your Inquira classrooms, class codes and student invitations." },
      { property: "og:title", content: "Manage your Inquira classes" },
      { property: "og:description", content: "Create classrooms, share join codes and manage rosters." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
});

interface ClassRow {
  id: string;
  name: string;
  subject: string | null;
  grade_level: string | null;
  class_period: string | null;
  term: string | null;
  join_code: string;
  archived: boolean;
}

function TeacherClasses() {
  const { user } = useAuth();
  const [rows, setRows] = useState<ClassRow[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: "",
    subject: "",
    grade_level: "",
    description: "",
    class_period: "",
    term: "",
  });

  const load = useCallback(async () => {
    if (!user) return;
    const { data } = await supabase
      .from("classes")
      .select("id, name, subject, grade_level, class_period, term, join_code, archived")
      .eq("teacher_id", user.id)
      .order("created_at", { ascending: false });
    setRows((data as ClassRow[]) ?? []);
  }, [user]);

  useEffect(() => { void load(); }, [load]);

  async function create() {
    if (!form.name.trim()) { toast.error(i18n.t("edu.teacher_classes_index.give_the_class_a_name")); return; }
    setSaving(true);
    const { error } = await eduRpc<string>("edu_create_class", {
      _name: form.name,
      _subject: form.subject || null,
      _grade_level: form.grade_level || null,
      _description: form.description || null,
      _class_period: form.class_period || null,
      _term: form.term || null,
    });
    setSaving(false);
    if (error) { toast.error(error); return; }
    toast.success(i18n.t("edu.teacher_classes_index.class_created"));
    setForm({ name: "", subject: "", grade_level: "", description: "", class_period: "", term: "" });
    setShowForm(false);
    void load();
  }

  async function toggleArchive(row: ClassRow) {
    const { error } = await eduRpc("edu_update_class", { _class_id: row.id, _archived: !row.archived });
    if (error) { toast.error(error); return; }
    void load();
  }

  return (
    <EduShell
      nav={TEACHER_NAV}
      title={i18n.t("edu.teacher_classes_index.classes")}
      subtitle={i18n.t("edu.teacher_classes_index.each_class_gets_its_own_code_roster_assi")}
      actions={<Button onClick={() => setShowForm((v) => !v)}>{showForm ? "Cancel" : "Create class"}</Button>}
    >
      {showForm && (
        <div className="mb-6 space-y-3 rounded-2xl border border-rule bg-card p-4">
          <input
            className="w-full rounded-xl border border-rule bg-background px-3 py-2 text-sm text-ink"
            placeholder={i18n.t("edu.teacher_classes_index.class_name_e_g_algebra_i_period_2")}
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
          <div className="grid gap-3 sm:grid-cols-2">
            <select
              className="rounded-xl border border-rule bg-background px-3 py-2 text-sm text-ink"
              value={form.subject}
              onChange={(e) => setForm({ ...form, subject: e.target.value })}
            >
              <option value=""><T k="edu.teacher_classes_index.subject" /></option>
              {SUBJECTS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <select
              className="rounded-xl border border-rule bg-background px-3 py-2 text-sm text-ink"
              value={form.grade_level}
              onChange={(e) => setForm({ ...form, grade_level: e.target.value })}
            >
              <option value=""><T k="edu.teacher_classes_index.level" /></option>
              {GRADE_LEVELS.map((g) => <option key={g} value={g}>{g}</option>)}
            </select>
            <input
              className="rounded-xl border border-rule bg-background px-3 py-2 text-sm text-ink"
              placeholder={i18n.t("edu.teacher_classes_index.class_period")}
              value={form.class_period}
              onChange={(e) => setForm({ ...form, class_period: e.target.value })}
            />
            <input
              className="rounded-xl border border-rule bg-background px-3 py-2 text-sm text-ink"
              placeholder={i18n.t("edu.teacher_classes_index.semester_term")}
              value={form.term}
              onChange={(e) => setForm({ ...form, term: e.target.value })}
            />
          </div>
          <textarea
            className="w-full rounded-xl border border-rule bg-background px-3 py-2 text-sm text-ink"
            rows={2}
            placeholder={i18n.t("edu.teacher_classes_index.description_optional")}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />
          <Button onClick={create} disabled={saving}>{saving ? "Creating…" : "Create class"}</Button>
        </div>
      )}

      {rows.length === 0 ? (
        <EmptyState title={i18n.t("edu.teacher_classes_index.no_classes_yet")} body="Create your first class and share the code with your students." />
      ) : (
        <ul className="grid gap-3 sm:grid-cols-2">
          {rows.map((row) => (
            <li key={row.id} className="rounded-2xl border border-rule bg-card p-4">
              <div className="flex items-start justify-between gap-2">
                <Link to="/teacher/classes/$classId" params={{ classId: row.id }} className="font-display text-lg font-bold text-ink">
                  {row.name}
                </Link>
                {row.archived && <span className="rounded-full bg-rule/40 px-2 py-0.5 text-xs text-ink-muted"><T k="edu.teacher_classes_index.archived" /></span>}
              </div>
              <p className="mt-1 text-sm text-ink-muted">
                {[row.subject, row.grade_level, row.class_period, row.term].filter(Boolean).join(" · ") || "No details yet"}
              </p>
              <div className="mt-3 flex items-center justify-between">
                <span className="rounded-lg bg-background px-2 py-1 font-mono text-sm text-ink">{row.join_code}</span>
                <button className="text-xs font-semibold text-ink-muted hover:text-ink" onClick={() => toggleArchive(row)}>
                  {row.archived ? "Restore" : "Archive"}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </EduShell>
  );
}
